import { faCompress, faExpand, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useMediaQuery from '@hooks/useMediaQuery';
import { cn } from '~/lib/cn';
import { motion } from 'framer-motion';
import { isEmpty } from 'lodash';
import React, { FC, HTMLAttributes, useState } from 'react';
import { requestModalClose } from './requestModalClose';
import { ModalWithId } from './useModalManagerStore';

const Modal: FC<React.PropsWithChildren<ModalWithId & Pick<HTMLAttributes<HTMLDivElement>, 'style'>>> = ({
    title,
    content,
    preventClose,
    confirmClose,
    id,
    size: _size,
    onClose,
    style,
    className,
    headerClassName,
    bodyClassName,
    bodyStyle
}) => {
    const [isFullScreen, setIsFullScreen] = useState<boolean | null>(Number(_size) === 100);
    const size = Number(isFullScreen ? 100 : _size || 60);
    const isMobile = useMediaQuery('(max-width: 1024px)');

    const handleClose = () => requestModalClose({ id, content, onClose, confirmClose });

    const toggleFullScreen = () => {
        setIsFullScreen((prev) => !prev);
    };

    return (
        <motion.div
            transition={{
                duration: 0.2,
                type: 'spring'
            }}
            initial={{ transform: 'translate(-50%, -50%) scale(0.9)' }}
            exit={{ transform: 'translate(-50%, -50%) scale(0.9)', opacity: 0 }}
            animate={{ transform: 'translate(-50%, -50%) scale(1)' }}
            style={{
                ...style,
                width: size && !isMobile ? `${size}vw` : undefined,
                minHeight: Number(size) >= 100 ? '100%' : undefined,
                borderRadius: Number(size) >= 100 ? 0 : undefined
            }}
            className={cn(
                'tw-absolute tw-left-1/2 tw-top-1/2 tw-flex tw-h-auto tw-max-h-[95vh] tw-min-h-[max(95vh,min-content)] tw-min-w-[95vw] tw-max-w-full tw-flex-col tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-pt-3 tw-shadow-2xl lg:tw-min-w-[20vw]',
                className
            )}
        >
            <div
                className={cn(
                    'tw-mb-2 tw-flex tw-min-h-min tw-items-center tw-justify-between tw-gap-4 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-px-4 tw-pb-2',
                    isEmpty(title) ? 'tw-border-transparent' : 'tw-border-accents-2',
                    headerClassName
                )}
            >
                {title && <span className="tw-text-2xl tw-font-semibold">{title}</span>}
                <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                    {Number(_size) < 100 && (
                        <button
                            onClick={toggleFullScreen}
                            className="tw-hover:bg-accents-1 tw-rounded-full tw-border-none tw-bg-transparent tw-p-1 tw-text-lg tw-text-accents-6 tw-transition-colors tw-duration-200"
                        >
                            <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} />
                        </button>
                    )}
                    {!preventClose && (
                        <button
                            onClick={handleClose}
                            className="tw-hover:bg-accents-1 tw-rounded-full tw-border-none tw-bg-transparent tw-p-1 tw-text-lg tw-text-accents-6 tw-transition-colors tw-duration-200"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                </div>
            </div>
            <div
                className={cn(
                    'tw-relative tw-flex tw-h-full tw-w-full tw-flex-1 tw-flex-col tw-overflow-y-auto tw-px-4 tw-pb-4',
                    bodyClassName
                )}
                style={{ scrollbarWidth: 'thin', scrollbarGutter: 'stable', ...bodyStyle }}
            >
                {content as React.ReactNode}
            </div>
        </motion.div>
    );
};

export default Modal;
